// client/src/hooks/useChatChannels.js

import { useState, useEffect, useCallback } from 'react'
import ApiChat from '../api/ApiChat'
import ChatService from '../services/ChatService'
import useAuth from './useAuth'

/**
 * Custom hook for managing chat channels
 * @returns {Object} Channels state and operations
 */
export default function useChatChannels() {
  const { isLoggedIn } = useAuth()
  const [channels, setChannels] = useState([])
  const [loadingChannels, setLoadingChannels] = useState(true)

  const loadChannels = useCallback(async () => {
    try {
      setLoadingChannels(true)
      const response = await ApiChat.getChannels()
      setChannels(response.channels || [])

      // Ensure we are joined to all channel rooms for real-time updates
      try {
        if (ChatService.isConnected && Array.isArray(response.channels)) {
          response.channels.forEach((c) => {
            try {
              ChatService.joinChannel(c.id)
            } catch {
              // Ignore individual channel join errors - non-critical
            }
          })
        }
      } catch {
        // Ignore channel joining errors - channels will still work via polling
      }

      return response.channels || []
    } catch (error) {
      console.error('Failed to load channels:', error)
      throw new Error('Failed to load channels')
    } finally {
      setLoadingChannels(false)
    }
  }, [])

  // Load channels on component mount and when external refresh requested
  useEffect(() => {
    if (!isLoggedIn) return

    loadChannels()

    const handler = () => loadChannels()
    window.addEventListener('chat:refresh-channels', handler)
    return () => window.removeEventListener('chat:refresh-channels', handler)
  }, [isLoggedIn, loadChannels])

  return {
    channels,
    loadingChannels,
    loadChannels,
  }
}
